/**
 * dashboardView.ts — pure view logic for the Sales Dashboard (P10 parity).
 * Kept component-free so vitest pins the behaviour (the closeFlow/leadsFlow
 * pattern).
 */
import type { SalesDeal, DealStage } from '../../types'

// The four OPEN stages (mockup pipeline order). Closed and Unknown are out.
const OPEN_STAGES: ReadonlySet<DealStage> = new Set<DealStage>([
  'New Lead', 'Qualified', 'Proposal Sent', 'Negotiation',
])

/**
 * Top open deals by value, mockup card shape (§10.F: 4 rows).
 *
 * HONEST CEILING: the input is the /v1/sales/deals list call, which is
 * capped at limit=200 newest-first (module.go ORDER BY created_at DESC) —
 * so this is "top of the newest 200", not a server-side global sort. The
 * D4-3 fix, if the cap ever bites, is a server sort param — not a bigger
 * client fetch.
 */
export function topOpenDeals(deals: SalesDeal[], n = 4): SalesDeal[] {
  // Ranked on the GBP-COMPARABLE leg (0111): original-currency pence are
  // not comparable across rows. An uncaptured row (basePence null — only
  // raw history could) has no honest rank and sorts last.
  return deals
    .filter((d) => OPEN_STAGES.has(d.stage))
    .sort((a, b) => (b.basePence ?? -1) - (a.basePence ?? -1))
    .slice(0, n)
}

/**
 * "Closed this month" in pence: the CURRENT month's closed-won value.
 * revenueByMonth is the server's zero-filled 6-bucket window, ascending
 * (ORDER BY m.month), so the last bucket IS the current month — no client
 * date math, no timezone re-derivation.
 */
export function closedThisMonthPence(monthlyRevenue: { month: string; won: number }[]): number {
  return monthlyRevenue.length > 0 ? monthlyRevenue[monthlyRevenue.length - 1].won : 0
}

export interface CompanyRollup {
  company: string
  total: number
  won: number
  lost: number
  /** total − won − lost: open stages AND Unknown (a stage this reader
   *  doesn't recognise is still not closed). */
  open: number
  /** Σ GBP-comparable pence (0111 whole sums: valuePence for GBP deals,
   *  captured base for non-GBP) over OPEN-stage deals only. */
  pipelinePence: number
  /** Σ GBP-comparable pence over Closed Won deals. */
  wonPence: number
  /** UNCAPTURED non-GBP deals (basePence null) — rows excluded from both
   *  sums above but still counted in total/won/lost/open. Structurally
   *  zero under capture-or-refuse; the honesty belt only. */
  excludedNonGbp: number
}

/**
 * Deals grouped by company — SB-2 item 5, deliberately CLIENT-SIDE over the
 * list the dashboard already fetches (recon decision: no endpoint at this
 * scale). Grouping is by the EXACT company string (trimmed): company is
 * free text on the deal (0033, no companies table), so case/spelling
 * variants split rows — the same v1 limit Top performers carries for rep
 * names; the honest fix is the deferred first-class companies table.
 * Same limit-200 ceiling as topOpenDeals.
 */
/**
 * The company drill's row set (SB-13): the SAME trimmed-exact-string match
 * dealsByCompany groups by, over the SAME list — so a drill can never show a
 * different set than the rollup row counted. No fuzzy matching by design:
 * "Acme" and "Acme Ltd" are different companies until companies become
 * first-class.
 */
export function companyDeals(deals: SalesDeal[], company: string): SalesDeal[] {
  return deals.filter((d) => d.company.trim() === company)
}

export function dealsByCompany(deals: SalesDeal[]): CompanyRollup[] {
  const groups = new Map<string, CompanyRollup>()
  for (const d of deals) {
    const company = d.company.trim()
    if (company === '') continue // server forbids it; never render a blank group
    let g = groups.get(company)
    if (!g) {
      g = { company, total: 0, won: 0, lost: 0, open: 0, pipelinePence: 0, wonPence: 0, excludedNonGbp: 0 }
      groups.set(company, g)
    }
    g.total++
    // Money sums use the GBP-COMPARABLE leg (0111); an uncaptured row is
    // excluded from the sums (never guessed) and counted in the belt.
    if (d.basePence === null) g.excludedNonGbp++
    if (d.stage === 'Closed Won') {
      g.won++
      g.wonPence += d.basePence ?? 0
    } else if (d.stage === 'Closed Lost') {
      g.lost++
    } else {
      g.open++
      if (OPEN_STAGES.has(d.stage)) g.pipelinePence += d.basePence ?? 0
    }
  }
  return [...groups.values()].sort((a, b) => b.total - a.total || a.company.localeCompare(b.company))
}
